import Util from './util';

import * as log from 'loglevel';
import {render} from 'lit-html';

// LAZY
//import Splide from '@splidejs/splide';
//import '@splidejs/splide/dist/css/splide.min.css';

import productGalleryTemplate from './template/product-gallery.template';
import ResourceLoader from './resource-loader';
import ProductPreview from './product-preview';
import {ThemeConfig} from './const';
import ConditionsEngine from './conditions-engine';

class ProductGallery {

    constructor(config, productForm, conditionsEngine, options) {
        // Default settings
        let defaults = {
            api: 'https://api.cloudlift.app',
            assets: 'https://assets.cloudlift.app',
            queryParams: {},
            selector: '.cl-gallery',
            position: '', // optional element position used with https://developer.mozilla.org/en-US/docs/Web/API/Element/insertAdjacentElement
            sticky: false,
            stickyMobile: 'overlay',
            zoom: null,
            maxHeight: 0, // passed to preview
            classNamePrefix: 'cl-po',
            product: {},
            variantId: null,
            galleryConfig: {
                exclude: [],
                hideThumbnails: false,
                hideThumbnailsMobile: false,
                layout: 'bottom',
                arrows: false,
                arrowsThumbnails: false,
                pagination: false,
                paginationMobile: false,
                paginationThumbnails: false,
                previewActive: false, // true = only reset preview.active flag if another live preview is selected (allows to keep sticky overlay),
                loader: false,
                maxWidth: 2500, // max main image width on gallery
                width: 0 // dynamically set to match available width
            }
        };

        this.config = config;

        // Merge options into defaults
        this.settings = Util.extend(defaults, options || {});

        if(window.clGallery){ // debug overwrite - remove after customers informed
            this.settings.galleryConfig = Util.extend(this.settings.galleryConfig, window.clGallery);
        }

        this._previews = [];

        this.media = []; // product media shown in the gallery

        this._productForm = productForm;

        this.conditionsEngine = conditionsEngine;

        // check if selector is already an element
        if(this.settings.selector !== null){ // null check to avoid missing container
            if(Util.isElement(this.settings.selector)){
                this.element = this.settings.selector;
            } else {
                this.element = document.querySelector(this.settings.selector);
            }
        }

        if (this.element != null) {

            log.info('--> new product-gallery: ', this.element);

            // resources loaded async
            this.resources = [
                this.settings.assets + '/static/lib/splidejs/splide.min.js',
                this.settings.assets + '/static/lib/splidejs/splide.min.css',
            ];

            // load resources if not already ( this.ready can be checked from outside with a promise.then )
            this.ready = ResourceLoader.loadOnce(this.resources, 'product-gallery',  {async: false })
                // wait for resources to be ready before init
                // IMPORTANT: only resolve ready after init
                .then(() =>  this._init());

            // DEV: (Splide import) setTimeout(() => { this._init(); }, 100);

        } else {
            log.warn('no elements with selector found: ', this.settings.selector);
        }
    }

    destroy(){

        // destroy previews
        if(this._previews){
            for (let i = 0; i < this._previews.length; i++) {
                const preview = this._previews[i];
                preview.destroy();
            }
        }

        // remove wrapper
        this.element.removeChild(this._wrapper);
    }

    render(){
        render(productGalleryTemplate(this.media, this.settings.galleryConfig), this._wrapper);
    }

    selectPreview(position){
        // select thumbnail > triggers preview selection
        this._splideThumbnails.go( this._getMediaIndex(position) );
    }

    // public: show the view with this name (case-insensitive) - queued until the gallery is ready
    // no gallery container, unknown name or missing media > warn and keep the current view
    selectView(name){
        if(!this.ready){
            log.warn('[live-options] selectView needs the app gallery: ', name);
            return Promise.resolve();
        }

        return this.ready.then(() => {
            const view = this.getViews().find(view => view.name.toLowerCase() === String(name).toLowerCase());

            if(!view || this._getMediaIndex(view.position) === -1){
                log.warn('[live-options] selectView unknown view: ', name);
            }
            // already on this slide > no-op (loop guard for the view-switch sync)
            else if(view.name !== this._currentViewName()){
                this.selectPreview(view.position);
            }
        });
    }

    // public: ordered { name, position } views - the rendered previews once _init ran (_wrapper), the preview config before
    getViews(){
        const previewConfigs = this._wrapper
            ? this._previews.map(preview => preview.previewConfig)
            : [this.config.previewConfig, ...(this.config.previewConfig.views || [])];

        return previewConfigs.map(previewConfig => ({ name: previewConfig.name || 'Main', position: previewConfig.position }));
    }

    _init() {

        // create wrapper to scope by uuid
        this._wrapper = document.createElement('div');
        this._wrapper.className = this.settings.classNamePrefix + '--gallery-wrapper';
        this._wrapper.setAttribute('id', this.config.uuid + '_gallery');

        // IMPORTANT: for _getContainerWidth to scale to full available dimensions  TODO check if required
        this._wrapper.style.width = '100%';

        // add wrapper to element with position support
        //https://developer.mozilla.org/en-US/docs/Web/API/Element/insertAdjacentElement
        if(this.settings.position){
            this.element.insertAdjacentElement(this.settings.position, this._wrapper);
            // case before/after -> update parent element (required for events & all this.element.querySelector methods)
            if(this.element !== this._wrapper.parentNode){
                this.element = this._wrapper.parentNode;
            }
        } else {
            // default child
            this.element.appendChild(this._wrapper);
        }

        // NEW: auto disable theme gallery selection (only causes issue - not needed with this ProductGallery active)
        ThemeConfig.themeGallery = false;

        // main config
        let previewConfigs = [ this.config.previewConfig ];
        // add additional views
        if(this.config.previewConfig.views.length){
            previewConfigs.push(... this.config.previewConfig.views);
        }

        this._filterProductMedia(previewConfigs);

        // NEW: gallery loader
        if(this.settings.galleryConfig.loader){
            this._addLoader();
        }

        // NEW: get gallery width to scale main images accordingly
        this.settings.galleryConfig.width = this.element.offsetWidth; // IMPORTANT: this.element instead of this._wrapper (which is 0px at this point)

        if(this.settings.galleryConfig.width === 0){
            log.warn('gallery width is 0px media images will not be scaled');
        }

        this.render();

        // DOCS: https://splidejs.com/v3/guides/options/
        this._splideMain = new Splide(this._wrapper.querySelector('.' + this.settings.classNamePrefix + '--gallery-main'), {
            //looks nice
            type: 'fade',
            keyboard: 'focused',
            lazyLoad: 'nearby', // always lazy load to limit to 3 images (previous, current, next)
            preloadPages: 1, // lazyLoad 1 page (loads previous + next = 3 pages)
            // only allow drag movement if not editorMode AND not hide thumbnails (we exclude non-preview images - only used when embedded in the theme gallery)
            drag: !this.settings.editorMode && !this.settings.galleryConfig.hideThumbnails,
            rewind: this.settings.galleryConfig.arrows, // NEW: only rewind if arrows are enabled (lazy loading loads less images without rewind)
            arrows: this.settings.galleryConfig.arrows,
            pagination: this.settings.galleryConfig.pagination || this.settings.galleryConfig.paginationMobile,
            classes: {
                pagination: 'splide__pagination' + (this.settings.galleryConfig.paginationMobile ? ' mobile': ''), // custom css for mobile only pagination
            }
        });

        // NEW: lazy loading with aspect ratio set by image media to avoid layout shifts
        this._splideMain.on( 'lazyload:loaded',  (img, slideComponent) => {
            requestAnimationFrame(() => {
                // NEW: only remove if slide is a fixed image - Live preview is removed the aspectRatio after preview:rendered (below)
                if(slideComponent.slide.querySelector('img')){
                    slideComponent.slide.style.aspectRatio = '';
                }
            });
        } );

        if(!this.settings.galleryConfig.hideThumbnails){

            let direction = (this.settings.galleryConfig.layout === 'left' || this.settings.galleryConfig.layout === 'right') ? 'ttb' : 'ltr';

            // NEW: lazy loading if more images fit on the width of the thumbnails
            // INFO: logic from slider does not work well with autoWidth (dynamic items per page based on width)
            // the lib sees each slide as its own page so we calculate the possible pages (images) to preload
            let lazyLoad = 'sequential'; // default load all images (almost like browser would load all images - no width checks)
            let preloadPages = 1;

            // could be a setting: this.settings.galleryConfig.lazyThumbnails
            // <script>window.clGalleryThumbLazy = false;</script>
            if(this._wrapper.offsetWidth && window.clGalleryThumbLazy !== false){

                let splideMinWidth = window.innerWidth > 768 ? 100 : 66; // mobile 66px

                preloadPages = Math.ceil(this._wrapper.offsetWidth / splideMinWidth);

                // left/right layout calculation is not correct - we avoid reducing preloadPages to avoid showing loading thumbnails
                if(direction !== 'ttb'){
                    // app loads prev/next image already so we can reduce the preloadPages by 2
                    preloadPages = Math.max(preloadPages - 2, 1);
                }

                // only if more images than fit on the screen
                if(this.media.length > preloadPages ){
                    lazyLoad = 'nearby';
                }
            }

            // DOCS: https://splidejs.com/v3/guides/options/
            this._splideThumbnails = new Splide(this._wrapper.querySelector('.' + this.settings.classNamePrefix + '--gallery-thumbnails'), {
                keyboard: 'focused',
                lazyLoad: lazyLoad,
                preloadPages: preloadPages,
                autoWidth: true,
                fixedHeight: 100, // fixed height useful for bottom layout - also required for left/right
                height: direction === 'ttb' ? (window.innerWidth > 768 ? 600 : 200) : 0, // fixed height required for the vertical slider (NEW: mobile only 200)
                direction: direction,
                rewind    : this.settings.galleryConfig.arrows, // NEW: only rewind if arrows are enabled (lazy loading loads less images without rewind)
                gap       : 3, // +2px border on the thumbnails
                arrows: this.settings.galleryConfig.arrowsThumbnails,
                pagination: this.settings.galleryConfig.paginationThumbnails,
                isNavigation: true, // allow click
                //focus     : 'center', only nice with more than 1 row of images (otherwise we prefer left align)
                breakpoints : {
                    '600': {
                        fixedHeight: 66, // smaller thumbnails
                    }
                },
                // could be a setting: this.settings.galleryConfig.dragThumbnails
                // <script>window.clGalleryThumbnailDrag = false;</script>
                drag: window.clGalleryThumbnailDrag !== false,
                dragMinThreshold: {
                    mouse: 30, // NEW: increase drag threshold to avoid accidental drag on click (mentioned by customers)
                    touch: 10 // default
                }
            });
            this._splideThumbnails.mount(); // mount before main

            // sync main with thumbnails & mount
            this._splideMain.sync( this._splideThumbnails ).mount();

        } else {
            // NEW: no thumbnail > keep rest of code that calls thumbnails for navigation working > map go method to main
            this._splideThumbnails = { go: (index) => {
                    this._splideMain.go(index);
                }};
            // mount main only
            this._splideMain.mount();
        }

        // Observe the main slide for every navigation path, including arrows/swipes without thumbnails.
        this._splideMain.on('moved', (newIndex) => this._selectPreview(newIndex));

        let previewReadyPromises = [];

        for (let i = 0; i < previewConfigs.length; i++) {

            let previewConfig = previewConfigs[i];

            let imgEl = this._wrapper.querySelector('.' + this.settings.classNamePrefix + '--gallery-main [data-media-position="'+previewConfig.position+'"]');

            if(imgEl){
                // add preview root on slide item
                let previewEl = imgEl.parentNode;
                // remove the img > preview adds canvas element
                previewEl.removeChild(imgEl);

                let productPreview = new ProductPreview(this.config, previewConfig, this._productForm, this.conditionsEngine,  {
                    api: this.settings.api, // map urls
                    assets: this.settings.assets,
                    selector: previewEl, // provide preview
                    selectorThumbnail: this.settings.selectorThumbnail, // map selector thumbnail (theme gallery thumbnail - ONLY used when ProductGallery.thumbnails = false)
                    sticky: this.settings.sticky, // map sticky flag
                    stickyMobile: this.settings.stickyMobile, // map stickyMobile flag
                    stickyMobileSize: this.settings.stickyMobileSize, // map stickyMobileSize
                    zoom: this.settings.zoom, // map zoom config
                    maxHeight: this.settings.maxHeight, // map maxHeight flag
                    product: this.settings.product,
                    variantId: this.settings.variantId, // current variant id ( update via events )
                    editorMode: this.settings.editorMode
                });

                // default first position is active (custom this.settings.galleryConfig.position triggers selectPreview which updates active states)
                productPreview.active = previewConfig.position === 1;

                /* EDGE case (no initial preview selected - but previewActive is set to show sticky overlay of the preview without initial position) */
                if(i === 0 && this.settings.galleryConfig.previewActive && isNaN(this.settings.galleryConfig.position)){
                    productPreview.active = true;
                }

                // auto select first active preview
                if(productPreview.active){
                    this._preview = productPreview;
                }

                // save thumbnail el (used for canvas dataUrl sync)
                if(!this.settings.galleryConfig.hideThumbnails){
                    productPreview._thumbnailEl = this._wrapper.querySelector('.' + this.settings.classNamePrefix + '--gallery-thumbnails [data-media-position="'+previewConfig.position+'"]');
                }

                // add to ready promises
                previewReadyPromises.push(productPreview.ready.then(() => {

                    // NEW: remove aspect ratio only AFTER preview is rendered (to avoid layout shifts)
                    productPreview.element.addEventListener('preview:rendered', () => {
                        // wait one more render frame to avoid layout shift
                        requestAnimationFrame(() => {
                            productPreview.element.closest('.splide__slide').style.aspectRatio = '';
                        });
                    }, {once: true});

                    // only if thumbnail is shown
                    if(productPreview._thumbnailEl){
                        const syncThumbnail = (e) => {
                            let options = {
                                format: 'png', // for transparent support
                                multiplier: 200 / productPreview._canvas.getHeight() // target height 200px requires dynamic multiplier based on the scaled height
                            }

                            // update thumbnail src with rendered canvas
                            productPreview._thumbnailEl.src = productPreview._canvas.toDataURL(options);
                            // required to render full canvas again (without this canvas is only white)
                            productPreview._canvas.requestRenderAll();
                        };

                        // layer change (interactive updates)
                        productPreview.element.addEventListener('layer:changed', syncThumbnail);
                        // rendered (options change)
                        productPreview.element.addEventListener('preview:rendered', syncThumbnail);
                    }
                }));

                this._previews.push(productPreview);
            } else {
                log.warn('[live-options] gallery - missing media image for view: ', previewConfig)
            }

        }

        this._watchFormChanges();

        // NEW: splide drag dynamic enable/disable - only required when the preview is not static (interactive/crop)
        if(this._splideMain.options.drag && !this._isStatic(previewConfigs)){
            this._watchPreviewChanges();
        }

        // editorMode full re-init - re select gallery position
        if(this.settings.editorMode && window.clProductGalleryPosition){
            this.selectPreview(window.clProductGalleryPosition);
            delete window.clProductGalleryPosition;
        }
        // optional default initial gallery position
        else if(!isNaN(this.settings.galleryConfig.position)) {
            this.selectPreview(this.settings.galleryConfig.position);
        } else {
            // auto select first variant thumbnail (to support ?variant= param)
            this._selectVariantThumbnail(this._getVariantById(this.settings.variantId));
        }

        // NEW: make sure a view is selected (misconfiguration can cause no view being selected - and then updates caused the gallery to re-initialize)
        if(!this._preview && this._previews.length){
            this._preview = this._previews[0];
        }

        // chain init ready promise (only call gallery ready.then() when all previews are ready)
        return Promise.all(previewReadyPromises);
    }

    _filterProductMedia(previewConfigs){

        this.media = this.settings.product.media.filter(media => {
            return this.settings.galleryConfig.exclude.indexOf(media.position) === -1;
        });

        // no thumbnails > remove all media that does not have a live preview
        if(this.settings.galleryConfig.hideThumbnails){
            this.media = this.media.filter(media => {
                for (let i = 0; i < previewConfigs.length; i++) {
                    const previewConfig = previewConfigs[i];
                    if(previewConfig.position === media.position){
                        return true;
                    }
                }
                return false;
            });
        }
    }

    _getMediaIndex(position){
        for (let i = 0; i < this.media.length; i++) {
            if(this.media[i].position === position){
                return i;
            }
        }
        return -1;
    }

    _getMediaPosition(index){
        if(index < this.media.length){
            return this.media[index].position;
        }
        return -1;
    }

    // view name of the preview on a media position - null for plain media without a live preview
    _viewName(position){
        for (let i = 0; i < this._previews.length; i++) {
            const previewConfig = this._previews[i].previewConfig;
            if(previewConfig.position === position){
                return previewConfig.name || 'Main'; // main preview can have no name (mirrors product-options._previewStateProperty)
            }
        }
        return null;
    }

    // view name of the current main slide (seeds the view-switch option)
    _currentViewName(){
        return this._viewName(this._getMediaPosition(this._splideMain.index));
    }

    _selectPreview(newIndex){
        if(this.media.length > newIndex){
            let media = this.media[newIndex];

            // reset active flag on previous preview
            // NEW: previewActive flag to avoid .active reset when the view is changed to a non-live preview
            if(!this.settings.galleryConfig.previewActive && this._preview){
                this._preview.active = false;
            }

            // set selected preview as active
            for (let i = 0; i < this._previews.length; i++) {
                const preview = this._previews[i];
                if(preview.previewConfig.position === media.position){

                    // reset active flag on previous preview
                    if(this._preview){
                        this._preview.active = false;
                    }

                    // store active reference
                    this._preview = preview;
                    this._preview.active = true;

                    break;
                }
            }

            // trigger active preview changed - NEW: on every slide with the view name (null on plain media) for the view-switch option & theme code
            this._triggerEvent('preview:active', { position: media.position, name: this._viewName(media.position) });

            // NEW: 3d model avoid interactive preview swipe
            if(media.media_type === 'model'){
                if(!this._splideMain.Components.Drag.disabled){
                    this._DragRestore = true;
                    this._splideMain.Components.Drag.disable(true);
                }
            }
            // restore if it drag was disabled
            else if(this._DragRestore){
                this._splideMain.Components.Drag.disable(false);
                delete this._DragRestore;
            }
        }
    }

    _selectVariantThumbnail(variant){

        // NEW: skip variant selection if the current active preview shows the current variant image
        if(this._preview && this._preview.active){
            let productLayer = this._findProductLayer(this._preview.previewConfig);

            if(productLayer && productLayer.params.variant && !productLayer.params.imgPos){ // NEW: exclude fixed position (config but - normally never both are active)

                // NEW: previewActive flag  make sure the active preview is shown when variant is changed (allows selecting thumbnails - then re-selecting the right live preview on variant changes)
                if(this.settings.galleryConfig.previewActive){
                    this._splideThumbnails.go(this._getMediaIndex(this._preview.previewConfig.position));
                }

                return;
            }
        }

        if(variant && (variant.featured_media || variant.featured_image)){ // featured media shopify page JS, rest api featured image
            let featured_media = variant.featured_media || variant.featured_image;
            // select thumbnail > triggers main select
            let mediaIndex = this._getMediaIndex(featured_media.position);
            if(mediaIndex !== -1){
                this._splideThumbnails.go(mediaIndex);
            } else {
                // no media index found > find the preview that is updated based on the variant
                for (let i = 0; i < this._previews.length; i++) {
                    const preview = this._previews[i];

                    let productLayer = this._findProductLayer(preview.previewConfig);

                    if(productLayer && productLayer.params.variant){
                        // get media for this preview
                        mediaIndex = this._getMediaIndex(preview.previewConfig.position);
                        if(mediaIndex !== -1){
                            this._splideThumbnails.go(mediaIndex);
                            return;
                        }
                    }
                }
            }
        }
    }

    _findProductLayer(previewConfig){
        for (let j = 0; j < previewConfig.layers.length; j++) {
            const layer = previewConfig.layers[j];
            if(layer.type === 'product'){
                return layer;
            }
        }
        return null;
    }

    _watchFormChanges(){
        if(this._productForm){
            // watch for product from option changes
            this._productForm.addEventListener('option:changed', (e) => this._optionChanged(e));
            this._productForm.addEventListener('option:crop', (e) => this._optionChanged(e)); // same logic as option changed to auto select view where the crop is executed
            this._productForm.addEventListener('option:variant_changed', (e) => this._variantChanged(e));
            this._productForm.addEventListener('option:tabs:changed', (e) => this._tabChanged(e));
        }
    }

    // NEW: option:tabs:changed > first view whose layers reference any (nested) option of the opened tab - stays when none or already shown
    _tabChanged(event){
        const flatten = (options, leafs = []) => {
            for (let i = 0; i < options.length; i++) {
                const option = options[i];
                // Group editors replace configured templates with instances during initialization.
                const children = option.type === 'group-editor' && option._controller?.templates
                    ? [...(option.options || []), ...Object.values(option._controller.templates)]
                    : option.options;
                children?.length ? flatten(children, leafs) : leafs.push(option);
            }
            return leafs;
        };

        let position = this._findViewForOptions(flatten([event.detail.tab]));

        if(position !== -1){
            this.selectPreview(position);
        }
    }

    _watchPreviewChanges(){
        this.element.addEventListener('preview:rendered', (e) => this._checkInteractive(e)); // preview contents changed
        this.element.addEventListener('preview:active', (e) => this._checkInteractive(e)); // new gallery preview selected
    }

    _variantChanged(event){
        // store variant id ( provided on render context )
        this.settings.variantId = event.detail.variantId;

        this._selectVariantThumbnail(this._getVariantById(this.settings.variantId));
    }

    _optionChanged(event){

        let firstMatch = this._findViewForOptions([event.detail]);

        // we select the first match ( -1 when the current position already matches )
        if(firstMatch !== -1){
            this.selectPreview(firstMatch);

            // HACK: race condition when the preview is changed for cropping
            // selectPreview already triggers _selectPreview via this._splideThumbnails.on('moved') event - but on larger galleries the event handler is executed after the productPreview handler
            // to fix we sync call it here to set .active before the productPreview._optionCrop handler checks the flag
            if(event && event.type === 'option:crop'){
                this._selectPreview(this._getMediaIndex(firstMatch));
            }
        }
    }

    // NEW: first preview position whose layers reference (option or condition) any of the given options - -1 if none OR the current slide already matches
    _findViewForOptions(options){

        let position = -1;
        let firstMatch = -1;

        // current position
        let galleryPosition = this._getMediaPosition(this._splideMain.index);

        // check if the changed option is shown on a single preview
        for (let i = 0; i < this._previews.length; i++) {
            const preview = this._previews[i];

            for (let o = 0; o < options.length; o++) {
                const option = options[o];

                /* multiple layers can point to the same option & we have to loop over the actual rendered objects to find the best match */
                if(preview._canvas){

                    let objects = preview._canvas.getObjects();

                    for (let i = 0; i < objects.length; i++) {
                        const object = objects[i];
                        // find the matching object layer
                        if (object._layer && object._layer.params.option === option.name) {

                            if(preview.previewConfig.position !== position){
                                position = preview.previewConfig.position;
                                // set first match
                                if(firstMatch === -1){
                                    firstMatch = position;
                                }
                            }
                            // single layer reference is enough to check this preview
                            break;
                        }
                    }
                }

                /* IMPORTANT: fallback to search on the layer config (object could not be rendered yet, e.g image upload crop) */
                if(position === -1){
                    for (let j = 0; j < preview.previewConfig.layers.length; j++) {
                        const layer = preview.previewConfig.layers[j];
                        // layer references the changed option - NEW: group-editor instances (e.g back_text_1) are nested below the top-level template layer > match the template name
                        // IMPORTANT: the _templateName guard is load bearing - undefined === undefined would match every layer without params.option (e.g product)
                        if(layer.params.option === option.name || (option._templateName && layer.params.option === option._templateName)){
                            if(preview.previewConfig.position !== position){
                                position = preview.previewConfig.position;
                                // set first match
                                if(firstMatch === -1){
                                    firstMatch = position;
                                }
                            }
                            // single layer reference is enough to check this preview
                            break;
                        }
                    }

                    // NEW: layer.conditions option linking (last effort to find a preview to auto select)
                    // INFO: allows to change views by checking if a "TRUE" evaluating condition is found on a layer that references the changed option
                    if(position === -1){
                        for (let j = 0; j < preview.previewConfig.layers.length; j++) {
                            const layer = preview.previewConfig.layers[j];
                            // check if this layer references the changed option in a condition
                            if(layer.conditions.length){

                                let match = false;

                                for (let k = 0; k < layer.conditions.length; k++) {
                                    const condition = layer.conditions[k];


                                    // check if the layer has a condition on the changed option
                                    if(condition.field === option.name){
                                        // check that the conditions matches
                                        if(this.conditionsEngine.evaluateOptionCondition(condition, [option])) {
                                            if(preview.previewConfig.position !== position){

                                                // NEW: full condition evaluation to make sure the view matches (e.g 4 views, 2 of the 4 share two options, but only one view matches the full condition)
                                                if(layer.conditions.length > 1){
                                                    if(!this.conditionsEngine.evaluateConditions(layer.conditions, layer.conditionMode || ConditionsEngine.MODE.AND, 'gallery_layer.name=' + layer.name)){
                                                        // no match on the full condition
                                                        continue;
                                                    }
                                                }

                                                position = preview.previewConfig.position;
                                                // set first match
                                                if(firstMatch === -1){
                                                    firstMatch = position;
                                                }
                                                match = true;
                                                break;
                                            }
                                        }
                                    }
                                }
                                // double for loop break again when match found
                                if(match){
                                    break;
                                }
                            }
                        }
                    }
                }

                // position already selected > skip
                if(position === galleryPosition){
                    return -1;
                } else {
                    // NEW: reset position to re-execute fallback checks for the multiple views
                    position = -1;
                }
            }
        }

        return firstMatch;
    }

    _getVariantById(variantId){ // copied from product-options.js
        if(this.settings.product.variants && this.settings.product.variants.length > 0){
            for (let k = 0; k < this.settings.product.variants.length; k++) {
                const variant = this.settings.product.variants[k];
                // compare with current variantId
                if(variant.id.toString() === variantId.toString()){
                    return variant;
                }
            }
        }
        return null;
    }

    _checkInteractive(e){

        let interactive = false;

        if(this._preview && this._preview._canvas){
            // cropMode flag directly (better than option:crop event)
            if(this._preview.settings.cropMode){
                interactive = true;
            } else {
                // check the actual rendered objects if they are interactive
                let objects = this._preview._canvas.getObjects();

                for (let i = 0; i < objects.length; i++) {
                    const object = objects[i];
                    // check if the object layer is interactive
                    // INFO: empty text layer with interactive is already triggered (to prevent this a 'is not empty' condition on the layer can be added)
                    if (object._layer && object._layer.params.interactive) {
                        interactive = true;
                        break;
                    }
                }
            }
        }

        // toggle drag disabled/enabled
        this._splideMain.Components.Drag.disable(interactive);
    }

    _isStatic(previewConfigs){

        for (let i = 0; i < previewConfigs.length; i++) {
            const previewConfig = previewConfigs[i];

            for (let i = 0; i < previewConfig.layers.length; i++) {
                const layer = previewConfig.layers[i];
                if(layer.params.interactive || (layer.params.crop && layer.type === 'upload')){
                    return false;
                }
            }
        }

        return true;
    }

    _addLoader(){

        let minHeight = '300px'; // default

        // we find the aspect ratio of the first shown media image
        let mediaIndex = this._getMediaIndex(isNaN(this.settings.galleryConfig.position) ? 1 : this.settings.galleryConfig.position);

        if(mediaIndex !== -1){
            let media = this.media[mediaIndex];

            if(media){
                let aspectRatio = media.aspect_ratio;

                // missing aspect ratio > calc based on dimension
                if(!aspectRatio && media.width && media.height){
                    aspectRatio = media.width / media.height;
                }

                if(aspectRatio){

                    // get available width to calculate the space the gallery would use up (this is done to avoid layout shifts)
                    let width = this.element.offsetWidth;

                    // INFO: on the setup the element can have empty width (we loop upwards to find a width)
                    let node = this._wrapper;
                    while(width === 0 && node.parentNode) { // edge case no parent node
                        // check parent until width is not 0
                        node = node.parentNode;
                        if(node.offsetWidth > 0){
                            // INFO: this size can include padding/margin (OK for the loader size calc) width = Util.findInnerDimensions(node).width;
                            width = node.offsetWidth;
                        }
                    }

                    if(width){
                        minHeight = Math.round(width / aspectRatio) + 'px';
                    }
                }
            }
        }

        log.debug('--> product-gallery loader minHeight:', minHeight);

        this._loader = document.createElement('div');
        this._loader.className = 'cl-po--gallery-loader';
        this._loader.style.minHeight = minHeight;
        // hard coded loader layout
        this._loader.innerHTML = '<div class="cl-spinner"><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div></div>';

        // IMPORTANT: insert before to show loader while gallery opacity is reset
        this.element.insertBefore(this._loader, this._wrapper);

        // hide gallery while keeping layout
        this._wrapper.style.opacity = '0';

        const onPreviewRendered = (e) => {

            // wait for the main active preview to
            if(this._preview === e.detail || !this._preview){ // || EDGE case no preview active (makes sure to always remove loader)
                this._removeLoader();
                // remove listener
                this.element.removeEventListener('preview:rendered', onPreviewRendered);
            }
        }

        // wait for the previews to be rendered
        this.element.addEventListener('preview:rendered', onPreviewRendered);
    }

    _removeLoader(){
        // show gallery
        this._wrapper.style.opacity = '1';
        // remove loader
        if(this._loader){
            this.element.removeChild(this._loader);
            this._loader = null;
        }
    }

    _triggerEvent(name, data){
        // custom event allow cancel via e.preventDefault & bubble to trigger up the DOM tree
        const event = new CustomEvent(name, { detail: data, cancelable: true, bubbles: true });
        // we currently use the root element to bubble up to the productForm
        this.element.dispatchEvent(event);
    }
}

export default ProductGallery;
